import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { consoleApi, type ReviewStatus } from '@/lib/consoleApi';
import { canSubmitSignoff } from '@/lib/signoffDecision';
import { Button } from '@/components/ui/button';

const STATUS_HINT: Record<ReviewStatus, string> = {
  signed: 'Signed — this content is approved and releasable.',
  pending: 'Pending — no sign-off recorded for the current content.',
  stale: 'The data changed since the last sign-off — re-review needed.',
  changes_requested: 'Changes were requested on the current content.',
};

export function SignOffPanel({
  registryId, status, version, versionHash, onSigned, approveBlocked,
}: {
  registryId: string; status: ReviewStatus; version: number; versionHash: string; onSigned: () => void;
  // Set when the registry is not fully authored: Approve is refused server-side
  // (422), so we disable it here and default to Request changes.
  approveBlocked?: { paths: string[] } | null;
}) {
  const [decision, setDecision] = useState<'approved' | 'changes_requested'>(
    approveBlocked ? 'changes_requested' : 'approved',
  );
  const [notes, setNotes] = useState('');
  const [confirming, setConfirming] = useState(false);

  const m = useMutation({
    mutationFn: () => consoleApi.signOff(registryId, { decision, notes: notes.trim() || null, reviewed_hash: versionHash }),
    onSuccess: () => { setNotes(''); setConfirming(false); onSigned(); },
  });

  // Approve can't proceed while the content is unauthored — mirror the server gate.
  const approveDisabled = Boolean(approveBlocked);
  const ready = canSubmitSignoff(decision, notes) && !(decision === 'approved' && approveDisabled);

  return (
    <section className="rounded border border-border bg-surface p-21">
      <div className="mb-8 flex items-center justify-between">
        <h2 className="text-h3 font-semibold">Sign-off</h2>
        <span className="font-mono text-[11px] text-text-muted">v{version} · {versionHash.slice(0, 12)}…</span>
      </div>
      <p className="mb-13 text-body-sm text-text-secondary">{STATUS_HINT[status]}</p>
      <div className="flex flex-col gap-8">
        <label className={`flex items-center gap-2 text-body-sm ${approveDisabled ? 'text-text-muted' : ''}`}>
          <input
            type="radio" name="decision" checked={decision === 'approved'}
            disabled={approveDisabled} onChange={() => setDecision('approved')}
          />
          Approve
          {approveDisabled ? (
            <span data-testid="approve-blocked" className="text-[12px] text-warning">
              — unavailable: {approveBlocked?.paths.length} cell{approveBlocked?.paths.length === 1 ? '' : 's'} need authoring
            </span>
          ) : null}
        </label>
        <label className="flex items-center gap-2 text-body-sm">
          <input type="radio" name="decision" checked={decision === 'changes_requested'} onChange={() => setDecision('changes_requested')} />
          Request changes
        </label>
        {decision === 'changes_requested' ? (
          <textarea
            data-testid="signoff-notes" value={notes} onChange={(e) => setNotes(e.target.value)}
            placeholder="What needs to change? (required)"
            className="min-h-[64px] w-full resize-y rounded border border-border-strong bg-bg px-13 py-8 text-body-sm text-text-primary placeholder:text-text-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus"
            rows={3}
          />
        ) : null}
        {!confirming ? (
          <Button
            data-testid="signoff-submit" size="sm" disabled={!ready || m.isPending}
            onClick={() => setConfirming(true)}
            className="self-start"
          >{decision === 'approved' ? 'Approve…' : 'Request changes…'}</Button>
        ) : (
          <div className="flex flex-wrap items-center gap-8">
            <span className="text-body-sm text-text-primary">
              {decision === 'approved'
                ? 'Record your clinical sign-off of the current content (marks it releasable)?'
                : 'Record a change request on the current content?'}
            </span>
            <Button data-testid="signoff-confirm" size="sm" disabled={m.isPending} onClick={() => m.mutate()}>
              {m.isPending ? 'Recording…' : 'Confirm'}</Button>
            <Button variant="outline" size="sm" onClick={() => setConfirming(false)}>Cancel</Button>
          </div>
        )}
        {m.isError ? <p className="text-body-sm text-flagged">Could not record the sign-off.</p> : null}
      </div>
    </section>
  );
}
